// version v_1.4.3.7 (FIXED AUTH LOOP + INPUT BUG RESOLVED)

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useCart } from './CartContext';
import { useNavigate, useLocation } from 'react-router-dom';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const { cart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [shippingAddress, setShippingAddress] = useState(null);

  const [authLoaded, setAuthLoaded] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // ---------------- RESET AUTH STATE ----------------
  const resetAuth = () => {
    setUser(null);
    setShippingAddress(null);
    setAuthLoaded(true);
    setAuthChecked(true);
  };

  // ---------------- FETCH PROFILE ----------------
  const fetchProfile = async () => {
    try {
      const { data } = await axios.get(
        `https://apimyshop.myidealtd.co.uk/api/users/profile?nocache=${Date.now()}`,
        { withCredentials: true }
      );

      setUser(data);
      await fetchShippingAddress();

    } catch (err) {
      resetAuth();
    } finally {
      setAuthLoaded(true);
      setAuthChecked(true);
    }
  };

  // ---------------- INIT ONLY ONCE ----------------
  useEffect(() => {
    fetchProfile();
  }, []);

  // ❌ FIX: REMOVED ROUTE-BASED REFRESH (THIS WAS CAUSING INPUT RESET BUG)
  // useEffect(() => {
  //   fetchProfile();
  // }, [location.pathname]);

  // ---------------- REGISTER ----------------
  const register = async ({ name, email, password }) => {
    try {
      await axios.post(
        'https://apimyshop.myidealtd.co.uk/api/users/register',
        { name, email, password },
        { withCredentials: true }
      );

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Registration failed'
      };
    }
  };

  // ---------------- LOGIN ----------------
  const login = async (email, password) => {
    try {
      await axios.post(
        'https://apimyshop.myidealtd.co.uk/api/users/login',
        { email, password },
        { withCredentials: true }
      );

      await fetchProfile();

      return { success: true };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.message || 'Login failed'
      };
    }
  };

  // ---------------- LOGOUT ----------------
  const logout = async () => {
    try {
      await axios.post(
        'https://apimyshop.myidealtd.co.uk/api/users/logout',
        {},
        { withCredentials: true }
      );
    } catch (err) {
      console.log('Logout error ignored');
    }

    resetAuth();

    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 100);
  };

  // ---------------- SHIPPING ----------------
  const fetchShippingAddress = async () => {
    try {
      const { data } = await axios.get(
        'https://apimyshop.myidealtd.co.uk/api/users/shipping',
        { withCredentials: true }
      );

      setShippingAddress(data?.shippingAddress || null);
    } catch (err) {
      setShippingAddress(null);
    }
  };

  const updateShippingAddress = async (newAddress) => {
    try {
      const { data } = await axios.put(
        'https://apimyshop.myidealtd.co.uk/api/users/shipping',
        { shippingAddress: newAddress },
        { withCredentials: true }
      );

      setShippingAddress(data?.shippingAddress || null);

      await sendOrderSummary(data.shippingAddress);

      return {
        success: true,
        message: 'Shipping address updated'
      };
    } catch (err) {
      return {
        success: false,
        message: 'Update failed'
      };
    }
  };

  // ---------------- ORDER SUMMARY ----------------
  const sendOrderSummary = async (newShippingAddress) => {
    if (!user || !cart?.length) return;

    const subtotal = cart.reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    const shipping = 5;
    const tax = subtotal * 0.1;
    const total = subtotal + shipping + tax;

    const summary = {
      products: cart.map(item => ({
        productId: item._id,
        name: item.name,
        price: item.price,
        quantity: item.quantity
      })),
      address: newShippingAddress,
      subtotal,
      shipping,
      tax,
      total
    };

    try {
      await axios.put(
        'https://apimyshop.myidealtd.co.uk/api/orders/summary',
        summary,
        { withCredentials: true }
      );
    } catch (err) {
      console.log('Order summary error ignored');
    }
  };

  // ---------------- STABLE CONTEXT VALUE ----------------
  const value = useMemo(() => ({
    user,
    authLoaded,
    shippingAddress,
    login,
    logout,
    register,
    updateShippingAddress,
    sendOrderSummary
  }), [user, authLoaded, shippingAddress]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);